import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Activity } from 'lucide-react'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { toast } from '@/lib/toast'
import { systemSettingsApi } from '@/api/endpoints'
import { queryKeys } from '@/config/queryConfig'
import type { DefaultEventCategory } from '@/types'
import { SectionSkeleton, SectionError, SaveStatus } from './SectionState'
import { defaultEventsViewQuery } from './prefetch'

// ============================================================================
// Default events view (#248) — the tab the Events page opens on, for every user
// ============================================================================
// One fleet-wide value; only a super admin changes it. Not the per-camera default
// (items 236/238): opening a camera still uses that camera's own tab.

const DEFAULT_EVENT_VIEWS: DefaultEventCategory[] = ['animal', 'human', 'other', 'all']

export function DefaultEventsViewSection() {
  const { t } = useTranslation('settings')
  const { t: te } = useTranslation('events')
  const queryClient = useQueryClient()

  const { data, isPending, isError, refetch } = useQuery(defaultEventsViewQuery)

  const mutation = useMutation({
    mutationFn: (category: DefaultEventCategory) => systemSettingsApi.setGlobalDefaultEventsView(category),
    onSuccess: (updated) => {
      // The Events page reads the same key, so it picks the new tab up at once.
      queryClient.setQueryData(queryKeys.systemSettings.defaultEventsView(), updated)
      toast.success(t('defaultEventsView.setTo', { label: te(`tabs.${updated.category}`) }))
    },
    onError: () => toast.error(t('defaultEventsView.updateFailed')),
  })

  return (
    <section className="space-y-4">
      <div>
        <h2 className="text-base font-semibold">{t('defaultEventsView.title')}</h2>
        <p className="text-xs text-muted-foreground">{t('defaultEventsView.description')}</p>
      </div>

      {isPending ? (
        <SectionSkeleton rows={1} />
      ) : isError && !data ? (
        <SectionError onRetry={() => refetch()} />
      ) : (
        <div className="rounded-lg border bg-card p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-400">
              <Activity className="h-4 w-4" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium">{t('defaultEventsView.cardTitle')}</p>
              <p className="text-xs text-muted-foreground">{t('defaultEventsView.cardHint')}</p>
              <SaveStatus pending={mutation.isPending} saved={mutation.isSuccess} />
            </div>
            <Select
              value={data?.category ?? 'all'}
              disabled={mutation.isPending}
              onValueChange={(value) => mutation.mutate(value as DefaultEventCategory)}
            >
              <SelectTrigger className="w-44 h-9" aria-label={t('defaultEventsView.cardTitle')}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DEFAULT_EVENT_VIEWS.map((c) => (
                  <SelectItem key={c} value={c}>{te(`tabs.${c}`)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}
    </section>
  )
}
